/**
 * Monitoring models.
 *
 * The monitoring endpoints live in crdt-prpsl-servc (8989) but under their own
 * `/mntrng` root, so they are modelled here rather than in the credit models.
 */

/**
 * One monitoring item — a measurable output on the way in, a finding on the way
 * out. Both are the same row to the backend; only `dscrptn` and `sts` differ.
 */
export interface MonitoringItemDto {
  /** `null` on a row that has never been saved; the record's key on one that has. */
  mntrngSeq: number | null;

  dscrptn: string;
  rmrks: string;

  /**
   * Status as the SAVE payload carries it: a pick-list `refCdSeq` from ref
   * group 38.
   *
   * The GETs do not echo this field — they return {@link stsKey} instead, so
   * read status through `monitoringStatusOf()` rather than off `sts` directly.
   */
  sts?: number;

  /** Status as the GETs return it — the same ref group 38 code as {@link sts}. */
  stsKey?: number;

  delFlg: number;

  /** Date of the monitoring event, `yyyy-MM-dd`. Present on the GETs only. */
  evntDt?: string;

  /** Audit / echo fields the GETs add; kept so a row round-trips intact. */
  [key: string]: any;
}

/** Status of a row whichever way round the backend spelled it. */
export function monitoringStatusOf(row: MonitoringItemDto): number | null {
  const value = row?.stsKey ?? row?.sts;

  return value === null || value === undefined ? null : Number(value);
}

/** One client row from GET /mntrng/clients, with its monitoring counts rolled up. */
export interface MonitoringClientDto {
  clientSeq: number;
  clntNm: string;
  delFlg: number;

  totalAgrdActns: number;
  dueActionCount: number;
  compliantCount: number;
  partiallyCompliantCount: number;
  nonCompliantCount: number;
  newFindingsCount: number;
  lastEvntDt: string | null;

  /** Audit fields the GET adds; kept so a row round-trips intact. */
  [key: string]: any;
}

/** Payload for POST /mntrng/save-mntrng. */
export interface SaveMonitoringPayload {
  clntSeq: number;

  /** Date of the monitoring event, `yyyy-MM-dd`. */
  evntDt: string;

  monitoringRequestList: MonitoringItemDto[];
}

/**
 * One parsed row of an uploaded monitoring report workbook.
 *
 * The parser keys every cell by its header text so a report with extra or
 * reordered columns still previews correctly, and the mapping onto
 * {@link MonitoringItemDto} happens in exactly one place.
 */
export interface ParsedMonitoringRow {
  /** 1-based worksheet row number, used to point at bad rows in the preview. */
  rowNumber: number;
  values: Record<string, string>;
}
